import { NoticeDomainService } from './domain';
import { NoticeCategoryFactory } from './entity/noticeCategoryFactory';
import { NoticeItemFactory } from './entity/noticeItemFactory';

import type { NoticeCategoryEntity } from './entity/noticeCategoryEntity';
import type { NoticeCategoryIRepository } from './entity/noticeCategoryIRepository';
import type { NoticeItemEntity } from './entity/noticeItemEntity';
import type { NoticeItemIRepository } from './entity/noticeItemIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { NoticeCategoryJson } from '@/types/domain/notice/entities/noticeCategoryEntity';
import type { NoticeItemJson } from '@/types/domain/notice/entities/noticeItemEntity';

/**
 * アプリケーションサービス
 */
export class NoticeApplicationService {
  private service!: NoticeDomainService;

  private noticeCategoryFactory = new NoticeCategoryFactory();

  private noticeItemFactory = new NoticeItemFactory();

  /**
   * Inject
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param noticeCategoryRepository noticeCategoryRepository
   * @param noticeRepository noticeRepository
   * @param logger ロガー
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    noticeCategoryRepository: NoticeCategoryIRepository,
    noticeRepository: NoticeItemIRepository,
    logger: Logger,
  ) {
    this.service = new NoticeDomainService(
      wagakoNurseryApi,
      noticeCategoryRepository,
      noticeRepository,
      logger,
    );
  }

  /**
   * カテゴリ一覧を取得する.
   * @param nurseryId nurseryId.
   */
  public async getCategoryList(nurseryId: string) {
    const entityList = await this.service.getCategoryList(nurseryId);
    const items = entityList.map((v: NoticeCategoryEntity) => v.toJsonData());
    return {
      /** データ */
      items,
    };
  }

  /**
   * 限定公開ページ一覧を取得する.
   * @param nurseryId nurseryId.
   * @param token token.
   */
  public async getNoticeItemList(nurseryId: string, categoryId: string, token: string | null) {
    const result = await this.service.getNoticeItemList(nurseryId, categoryId, token);
    const { nextToken } = result;
    const items = result.items.map((v: NoticeItemEntity) => v.toJsonData());
    return {
      /** Nextトークン */
      nextToken,
      /** データ */
      items,
    };
  }

  /**
   * コンテンツをロードする.
   * @param item Json.
   * @param nurseryId string.
   */
  public async loadContentNotice(
    item: NoticeItemJson,
    nurseryId: string,
  ) {
    const entity = this.noticeItemFactory.createFromJsonPvt(item);
    return this.service.loadContent(entity.s3Path, nurseryId);
  }

  /**
   * アイテムリストを更新(Create)
   * @param noticeList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createNoticeStore(
    noticeList: NoticeItemJson[],
    payload: EntitySubscribeType,
    filter: ((json: NoticeItemJson) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePvt(
      noticeList,
      payload,
      this.noticeItemFactory,
      filter,
    );
  }

  /**
   * アイテムリストを更新(Update)
   * @param noticeList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateNoticeStore(
    noticeList: NoticeItemJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePvt(
      noticeList,
      payload,
      this.noticeItemFactory,
    );
  }

  /**
   * アイテムリストを更新(Delete)
   * @param noticeList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteNoticeStore(
    noticeList: NoticeItemJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePvt(
      noticeList,
      payload,
      this.noticeItemFactory,
    );
  }

  /**
   * カテゴリリストを更新(Create)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createNoticeCategoryStore(
    categoryList: NoticeCategoryJson[],
    payload: EntitySubscribeType,
    filter: ((json: NoticeCategoryJson) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePvt(
      categoryList,
      payload,
      this.noticeCategoryFactory,
      filter,
    );
  }

  /**
   * カテゴリリストを更新(Update)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateNoticeCategoryStore(
    categoryList: NoticeCategoryJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePvt(
      categoryList,
      payload,
      this.noticeCategoryFactory,
    );
  }

  /**
   * カテゴリリストを更新(Delete)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteNoticeCategoryStore(
    categoryList: NoticeCategoryJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePvt(
      categoryList,
      payload,
      this.noticeCategoryFactory,
    );
  }
}
